/**
 * TourTooltip - Positioned tooltip for tour steps
 *
 * Features:
 * - Auto-positions relative to target element
 * - Shows step title, content, and navigation
 * - Progress indicator
 * - Keyboard shortcuts hint
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, ArrowRight, X, Check,
  ChevronLeft, ChevronRight,
  Volume2, VolumeX
} from 'lucide-react';

// Placement configurations
const PLACEMENTS = {
  top: { vertical: 'above', horizontal: 'center' },
  'top-start': { vertical: 'above', horizontal: 'start' },
  'top-end': { vertical: 'above', horizontal: 'end' },
  bottom: { vertical: 'below', horizontal: 'center' },
  'bottom-start': { vertical: 'below', horizontal: 'start' },
  'bottom-end': { vertical: 'below', horizontal: 'end' },
  left: { vertical: 'center', horizontal: 'before' },
  'left-start': { vertical: 'start', horizontal: 'before' },
  'left-end': { vertical: 'end', horizontal: 'before' },
  right: { vertical: 'center', horizontal: 'after' },
  'right-start': { vertical: 'start', horizontal: 'after' },
  'right-end': { vertical: 'end', horizontal: 'after' },
};

function TourTooltip({
  step,
  targetElement,
  stepIndex,
  totalSteps,
  isFirstStep,
  isLastStep,
  voiceEnabled = true,
  onNext,
  onPrev,
  onSkip,
  onComplete,
  onToggleVoice,
}) {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] = useState(step?.placement || 'bottom');

  // Calculate tooltip position
  useEffect(() => {
    if (!tooltipRef.current) return;

    const calculatePosition = () => {
      const tooltip = tooltipRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const gap = 16; // Gap between target and tooltip

      // If no target, center in viewport
      if (!targetElement) {
        setPosition({
          top: window.innerHeight / 2 - tooltipRect.height / 2,
          left: window.innerWidth / 2 - tooltipRect.width / 2,
        });
        return;
      }

      const targetRect = targetElement.getBoundingClientRect();
      const placement = step?.placement || 'bottom';
      const config = PLACEMENTS[placement] || PLACEMENTS.bottom;

      let top = 0;
      let left = 0;
      let finalPlacement = placement;

      // Calculate vertical position
      switch (config.vertical) {
        case 'above':
          top = targetRect.top - tooltipRect.height - gap;
          // Flip to bottom if not enough space
          if (top < 10) {
            top = targetRect.bottom + gap;
            finalPlacement = placement.replace('top', 'bottom');
          }
          break;
        case 'below':
          top = targetRect.bottom + gap;
          // Flip to top if not enough space
          if (top + tooltipRect.height > window.innerHeight - 10) {
            top = targetRect.top - tooltipRect.height - gap;
            finalPlacement = placement.replace('bottom', 'top');
          }
          break;
        case 'center':
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          break;
        case 'start':
          top = targetRect.top;
          break;
        case 'end':
          top = targetRect.bottom - tooltipRect.height;
          break;
        default:
          top = targetRect.bottom + gap;
      }

      // Calculate horizontal position
      switch (config.horizontal) {
        case 'before':
          left = targetRect.left - tooltipRect.width - gap;
          // Flip to right if not enough space
          if (left < 10) {
            left = targetRect.right + gap;
            finalPlacement = placement.replace('left', 'right');
          }
          break;
        case 'after':
          left = targetRect.right + gap;
          // Flip to left if not enough space
          if (left + tooltipRect.width > window.innerWidth - 10) {
            left = targetRect.left - tooltipRect.width - gap;
            finalPlacement = placement.replace('right', 'left');
          }
          break;
        case 'center':
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'start':
          left = targetRect.left;
          break;
        case 'end':
          left = targetRect.right - tooltipRect.width;
          break;
        default:
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      }

      // Clamp to viewport
      top = Math.max(10, Math.min(top, window.innerHeight - tooltipRect.height - 10));
      left = Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10));

      setPosition({ top, left });
      setActualPlacement(finalPlacement);
    };

    // Initial calculation
    calculatePosition();

    // Recalculate on scroll/resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [targetElement, step?.placement]);

  // Progress percentage
  const progress = ((stepIndex + 1) / totalSteps) * 100;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[9999] w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in"
      style={{
        top: position.top,
        left: position.left,
        transition: 'top 0.3s ease-out, left 0.3s ease-out',
      }}
    >
      {/* Progress bar */}
      <div className="h-1 bg-gray-100">
        <div
          className="h-full bg-amber-500 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3">
        <span className="text-xs font-medium text-amber-600">
          Etape {stepIndex + 1} / {totalSteps}
        </span>
        <div className="flex items-center gap-1">
          {onToggleVoice && (
            <button
              onClick={onToggleVoice}
              className={`p-1 rounded-lg transition-colors ${
                voiceEnabled
                  ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={voiceEnabled ? 'Désactiver la voix' : 'Activer la voix'}
              aria-label={voiceEnabled ? 'Désactiver la voix' : 'Activer la voix'}
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={onSkip}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Fermer (Echap)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {step?.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed">
          {step?.content}
        </p>
      </div>

      {/* Step indicators */}
      <div className="flex justify-center gap-1.5 pb-3">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === stepIndex
                ? 'bg-amber-500'
                : i < stepIndex
                ? 'bg-amber-300'
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-100">
        {/* Previous button */}
        <button
          onClick={onPrev}
          disabled={isFirstStep}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isFirstStep
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Precedent</span>
        </button>

        {/* Next / Complete button */}
        {isLastStep ? (
          <button
            onClick={onComplete}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Check className="w-4 h-4" />
            <span>Terminer</span>
          </button>
        ) : (
          <button
            onClick={onNext}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <span>Suivant</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Keyboard hint */}
      <div className="px-4 py-2 bg-gray-100 text-xs text-gray-500 text-center">
        Utilisez <kbd className="px-1 py-0.5 bg-white rounded border">←</kbd>{' '}
        <kbd className="px-1 py-0.5 bg-white rounded border">→</kbd> pour naviguer,{' '}
        <kbd className="px-1 py-0.5 bg-white rounded border">Echap</kbd> pour fermer
      </div>
    </div>
  );
}

export default TourTooltip;
